import { memo, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  applyTableTemplate,
  columnById,
  defaultColumnState,
  deleteTableTemplate,
  differsFromTemplate,
  isDefaultColumnState,
  moveColumnBy,
  renameTableTemplate,
  saveTableTemplate,
  setColumnVisible,
  templatesFor,
  type ColumnState,
  type TableDef,
} from "@helm/core";
import { usePopover } from "../components/Popover";
import { Icon } from "../components/Icon";
import { useTableTemplates } from "./useTableColumns";

/**
 * Column picker (Tradovate's View Settings → Select Columns, NinjaTrader's Properties → Columns):
 * the gear in a table's toolbar. A searchable checklist grouped by category (each column's
 * definition as its tooltip), the shown columns in order with ↑/↓ (the keyboard alternative to
 * dragging headers), and table templates: apply, save as, rename, delete, reset to default.
 *
 * Closed, it is only the gear: the panel (every column, the templates) is built while open only,
 * and the gear is memoized, so a table that re-renders with each quote does not re-render it.
 */
function ColumnPickerOf<R>({
  def,
  state,
  onChange,
}: {
  def: TableDef<R>;
  state: ColumnState;
  onChange: (s: ColumnState) => void;
}): React.JSX.Element {
  const pop = usePopover();
  // The search outlives the panel: reopening shows the same filter.
  const [query, setQuery] = useState("");
  return (
    <>
      <button
        type="button"
        className={pop.open ? "icon-btn icon-btn-sm active" : "icon-btn icon-btn-sm"}
        aria-label={`${def.label} columns and templates`}
        title="Columns and templates"
        aria-haspopup="dialog"
        {...pop.triggerProps}
      >
        <Icon name="gear" size={14} />
      </button>
      {pop.open && (
        <PickerPanel
          pop={pop}
          def={def}
          state={state}
          onChange={onChange}
          query={query}
          setQuery={setQuery}
        />
      )}
    </>
  );
}

export const ColumnPicker = memo(ColumnPickerOf) as typeof ColumnPickerOf;

/** The open picker, portalled to the gear's own document (pop-out windows have their own). */
function PickerPanel<R>({
  pop,
  def,
  state,
  onChange,
  query,
  setQuery,
}: {
  pop: ReturnType<typeof usePopover>;
  def: TableDef<R>;
  state: ColumnState;
  onChange: (s: ColumnState) => void;
  query: string;
  setQuery: (q: string) => void;
}): React.JSX.Element {
  const [pos, setPos] = useState<{
    top: number | "auto";
    bottom: number | "auto";
    right: number;
    maxHeight: number;
  } | null>(null);
  const trigger = pop.triggerProps.ref;
  const search = useRef<HTMLInputElement | null>(null);

  // Placed under the gear (above it when there is more room there, e.g. a bottom dock), so a short
  // docked panel does not clip it.
  useLayoutEffect(() => {
    const place = (): void => {
      const el = trigger.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const win = el.ownerDocument.defaultView ?? window;
      const right = Math.max(8, win.innerWidth - r.right);
      const below = win.innerHeight - r.bottom - 16;
      const above = r.top - 16;
      if (below < 360 && above > below)
        setPos({ top: "auto", bottom: win.innerHeight - r.top + 4, right, maxHeight: above });
      else setPos({ top: r.bottom + 4, bottom: "auto", right, maxHeight: Math.max(160, below) });
    };
    place();
    const win = trigger.current?.ownerDocument.defaultView ?? window;
    win.addEventListener("resize", place);
    return () => {
      win.removeEventListener("resize", place);
    };
  }, [trigger]);

  const shown = new Set(state.columns);
  const q = query.trim().toLowerCase();
  const matches = def.columns.filter(
    (c) => q === "" || c.label.toLowerCase().includes(q) || c.definition.toLowerCase().includes(q),
  );
  const categories = [...new Set(def.columns.map((c) => c.category))];
  const modified = !isDefaultColumnState({ ...state, sort: null }, def);

  const panel = (
    <div
      {...pop.panelProps}
      className="popover col-picker"
      role="dialog"
      aria-label={`${def.label} columns`}
      style={
        pos
          ? {
              position: "fixed",
              top: pos.top,
              bottom: pos.bottom,
              right: pos.right,
              left: "auto",
              maxHeight: pos.maxHeight,
            }
          : { visibility: "hidden" }
      }
    >
      <TemplateBar def={def} state={state} onChange={onChange} />
      <div className="col-picker-row">
        <input
          ref={search}
          className="input input-sm col-picker-search"
          type="search"
          placeholder="Search columns…"
          aria-label="Search columns"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
        />
        <button
          type="button"
          className="btn btn-sm"
          disabled={!modified}
          title="Show Tradovate's default columns at default widths"
          onClick={() => {
            onChange({
              ...defaultColumnState(def),
              sort: state.sort && def.defaults.includes(state.sort.id) ? state.sort : null,
            });
            // The button disables itself: keep focus (and Escape) inside the picker.
            search.current?.focus();
          }}
        >
          Reset to default
        </button>
      </div>
      <div className="col-picker-body">
        <section aria-label="Shown columns, in order">
          <div className="menu-label">Shown · in order</div>
          <ol className="col-order">
            {state.columns.map((id, i) => {
              const c = columnById(def, id);
              if (!c) return null;
              return (
                <li key={id}>
                  <span className="col-order-name" title={c.definition}>
                    {c.label}
                  </span>
                  <button
                    type="button"
                    className="icon-btn icon-btn-xs"
                    aria-label={`Move ${c.label} left`}
                    title="Move left"
                    disabled={i === 0}
                    onClick={() => {
                      onChange(moveColumnBy(state, id, -1));
                    }}
                  >
                    <Icon name="chevronDown" size={12} className="icon-flip" />
                  </button>
                  <button
                    type="button"
                    className="icon-btn icon-btn-xs"
                    aria-label={`Move ${c.label} right`}
                    title="Move right"
                    disabled={i === state.columns.length - 1}
                    onClick={() => {
                      onChange(moveColumnBy(state, id, 1));
                    }}
                  >
                    <Icon name="chevronDown" size={12} />
                  </button>
                </li>
              );
            })}
          </ol>
        </section>
        <section aria-label="All columns">
          {categories.map((cat) => {
            const cols = matches.filter((c) => c.category === cat);
            if (cols.length === 0) return null;
            return (
              <fieldset key={cat} className="col-group">
                <legend className="menu-label">{cat}</legend>
                {cols.map((c) => (
                  <label
                    key={c.id}
                    className="col-check"
                    title={c.pending ? `${c.definition}\n\nPending: ${c.pending}` : c.definition}
                  >
                    <input
                      type="checkbox"
                      data-col={c.id}
                      checked={shown.has(c.id)}
                      disabled={c.locked === true || (shown.has(c.id) && state.columns.length <= 1)}
                      onChange={(e) => {
                        onChange(setColumnVisible(state, def, c.id, e.target.checked));
                      }}
                    />
                    <span>{c.label}</span>
                    {c.locked === true && <span className="muted"> · always shown</span>}
                    {c.pending !== undefined && <span className="col-pending"> · pending feed</span>}
                  </label>
                ))}
              </fieldset>
            );
          })}
          {matches.length === 0 && <div className="muted pad-x">No column matches “{query}”.</div>}
        </section>
      </div>
    </div>
  );

  return createPortal(panel, trigger.current?.ownerDocument.body ?? document.body);
}

type Mode =
  { kind: "idle" } | { kind: "save"; name: string } | { kind: "rename"; name: string } | { kind: "delete" };

/** Template select + Save as / Rename / Delete. Built-ins are read-only. */
function TemplateBar<R>({
  def,
  state,
  onChange,
}: {
  def: TableDef<R>;
  state: ColumnState;
  onChange: (s: ColumnState) => void;
}): React.JSX.Element {
  const { doc, save } = useTableTemplates();
  const [mode, setMode] = useState<Mode>({ kind: "idle" });
  const mine = templatesFor(doc, def.id);
  const builtIns = def.builtIns ?? [];
  const current =
    mine.find((t) => t.id === state.template) ?? builtIns.find((b) => b.id === state.template) ?? null;
  const userTemplate = mine.find((t) => t.id === state.template) ?? null;
  const modified = current !== null && differsFromTemplate(state, current);

  const commitSave = (name: string): void => {
    const r = saveTableTemplate(doc, { table: def.id, name, state, newId: `tpl-${crypto.randomUUID()}` });
    if (r.id === null) return;
    save(r.doc);
    onChange({ ...state, template: r.id });
    setMode({ kind: "idle" });
  };

  return (
    <div className="col-templates">
      <div className="col-picker-row">
        <label className="inline-field">
          <span className="muted">Template</span>
          <select
            className="select select-sm"
            value={current?.id ?? ""}
            aria-label="Apply a column template"
            onChange={(e) => {
              const id = e.target.value;
              const t = mine.find((x) => x.id === id) ?? builtIns.find((x) => x.id === id);
              if (t) onChange(applyTableTemplate(state, t, def));
              else onChange({ ...state, template: null });
              setMode({ kind: "idle" });
            }}
          >
            <option value="">{current === null ? "Custom" : "— none —"}</option>
            <optgroup label="Built-in">
              {builtIns.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </optgroup>
            {mine.length > 0 && (
              <optgroup label="Saved">
                {mine.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </label>
        {modified && <span className="col-pending">modified</span>}
      </div>
      {mode.kind === "save" || mode.kind === "rename" ? (
        <form
          className="col-picker-row"
          onSubmit={(e) => {
            e.preventDefault();
            if (mode.kind === "save") commitSave(mode.name);
            else if (userTemplate) {
              save(renameTableTemplate(doc, userTemplate.id, mode.name));
              setMode({ kind: "idle" });
            }
          }}
        >
          <input
            className="input input-sm"
            aria-label={mode.kind === "save" ? "New template name" : "Template name"}
            placeholder="Template name"
            maxLength={40}
            autoFocus
            value={mode.name}
            onChange={(e) => {
              setMode({ ...mode, name: e.target.value });
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.stopPropagation();
                setMode({ kind: "idle" });
              }
            }}
          />
          <button type="submit" className="btn btn-sm btn-primary" disabled={mode.name.trim() === ""}>
            {mode.kind === "save" ? "Save" : "Rename"}
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              setMode({ kind: "idle" });
            }}
          >
            Cancel
          </button>
        </form>
      ) : mode.kind === "delete" && userTemplate ? (
        <div className="col-picker-row" role="alert">
          <span>Delete “{userTemplate.name}” for every panel?</span>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() => {
              save(deleteTableTemplate(doc, userTemplate.id));
              onChange({ ...state, template: null });
              setMode({ kind: "idle" });
            }}
          >
            Delete
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              setMode({ kind: "idle" });
            }}
          >
            Keep
          </button>
        </div>
      ) : (
        <div className="col-picker-row">
          <button
            type="button"
            className="btn btn-sm"
            title="Save these columns and widths as a template (same name overwrites)"
            onClick={() => {
              setMode({ kind: "save", name: userTemplate?.name ?? "" });
            }}
          >
            Save as…
          </button>
          <button
            type="button"
            className="btn btn-sm"
            disabled={userTemplate === null}
            onClick={() => {
              if (userTemplate) setMode({ kind: "rename", name: userTemplate.name });
            }}
          >
            Rename
          </button>
          <button
            type="button"
            className="btn btn-sm"
            disabled={userTemplate === null}
            onClick={() => {
              setMode({ kind: "delete" });
            }}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
